/**
 * bimDeviceModel — a DEVICE's own 3D model slot (card t_ecc4a6e8).
 *
 * The owner, 2026-10-10: *"there is now way to create, add other ifc models.. set
 * the paramaters like the colors of the blindes.. we should import the ifc model,
 * like we import device thumbs.. each device would have optional ifc model, to
 * select. also to import from library.. device ifc should be added the same way
 * as device icon.. also would be nice to make it work without additional widget?
 * or we embed the icon widget to the same ui element"*.
 *
 * So the 3D presence of a device is part of the element that ALREADY carries it —
 * the `device-icon` widget (placement + the device binding) — and this module
 * owns the stored shape of that presence:
 *
 *   widget.widgetConfig.model3d = {
 *     source: 'library' | 'ifc',
 *     library: { type, model, prefs },        // the parametric family + its params
 *     ifc:     { url, name, bytes, uploadedAt, scale },   // an imported file
 *     placement: { origin, yawDeg, widthMm, dropFullMm },
 *     driverId: string | null                 // the device whose values drive it
 *   }
 *
 * Card t_6b2c5f01 moved the slot's HOME to the DEVICE — the owner's rule all
 * along: *"device ifc should be added the same way as device icon"*, and an icon
 * belongs to the device, not to a dashboard widget. The device's own `settings`
 * bag carries the very same slot under the same key (`settings.model3d`), and
 * `resolveDeviceModel()` is the one reader that puts the two together: the
 * device's slot first, the widget's stored one as the back-compat fallback for
 * slots written before that move. The geometry (family, params, an imported
 * file) belongs to the device; the PLACEMENT belongs to the element that carries
 * it — the widget's pin — so a device pinned in two places draws in both.
 *
 * `placement.origin` is the widget's own anchor and its frame is the SCENE's
 * frame — which `utils/bimFrame.ts` pins as plain IFC metres ("the scene's world
 * coordinates ARE IFC coordinates"), so a model attached here lands on the pin
 * the operator dropped and needs no second frame to keep in sync.
 *
 * The frame a model is BUILT in is the placement frame (`placementAxes`): `+X`
 * along the mounting plane's width, `+Y` up the plane, `+Z` the plane's outward
 * normal — and the ANCHOR is the centre of the fully-expanded shade, so the
 * head box stands half a full drop above the pin and the icon button the
 * operator placed sits on the middle of the extended shade (card t_ad566d07,
 * `shadePartsInDeviceFrame`). `pitchDeg` leans that frame back for a sloped
 * mounting plane (a roof window), which is the roof shutters' own variant.
 *
 * WHERE that plane is, is the element's own business (card t_9a056ea5): the
 * widget's anchor carries the surface normal the app already re-seats on every
 * load, so `mountingPlane()` hands it to `placementAxes()` and the two stored
 * numbers become the operator's TURN and extra LEAN from the window — 0 is flat
 * in the window. Read that block's comment before touching either number: the
 * absolute reading is exactly what shipped perpendicular shades when the served
 * model was axis-aligned under slots that had been written in the old frame.
 *
 * The library families are the SAME parametric builders the hub's house-ifc
 * pipeline uses (`otto-design/DemoHouse/house-ifc/devices/<family>/schema.json`, ported
 * for the live layer in `utils/bimShadeParts.ts`), and their defaults are those
 * schemas' own defaults (RAL 7016, hidden cassette, 78 mm slat pitch, 3 %
 * openness) — an attach made in the app renders what the house's own twins render.
 *
 * The LUMINAIRE family (card t_d267b30d) is the fourth one, and the reason the
 * module carries a device CLASS: a light device has no shade to attach, so the
 * library offers it the fixture the house itself builds for a lamp
 * (`utils/bimLuminaireParts.ts`), with the module's own params (variant, accent
 * tone, per-variant size) and the live behaviour a light has — it GLOWS with
 * the device's power (`utils/bimLuminaireSpec.ts` → `utils/bimGlow.ts`), exactly
 * like the baked twin the live layer drives. `DEVICE_MODEL_KIND_BY_FAMILY` /
 * `libraryFamilies()` are the one rule for "which list does this device see".
 *
 * Everything here is pure — no Vue, no three.js, no stores — so the stored shape,
 * the parameter resolution and the file-unit heuristic are unit-testable
 * (`bimDeviceModel.test.ts`), and the renderer (`useBimDeviceModels.ts`) only
 * turns these numbers into meshes.
 */

import type { JsonRecord, JsonValue } from '@hub/types/json';
import {
  VENETIAN,
  shadeParts,
  type ShadePartBox,
  type ShadePartMaterial,
  type StyledShadeParams,
} from './bimShadeParts';
import { GARAGE_DOOR, garageDoorParts, type GarageDoorPartMaterial } from './bimGarageDoorParts';
import { SLIDING_GATE, SWING_GATE, slidingGateParts, swingGateParts, type GatePartMaterial } from './bimGateParts';
import { waterTankShape, type WaterTankShape } from './bimWaterTankParts';
import type { Vec3 } from './bimShadeSpec';
import {
  isLuminaireAccent,
  isLuminaireVariant,
  type LuminaireAccent,
  type LuminaireVariant,
} from './bimLuminaireSpec';
import { BEAM_LIMITS, clampCct } from './bimFixtureLights';
import { isLuminaireMount, mountForVariant, type LuminaireMount } from './bimLuminaireMount';

export type { Vec3 };

/** Where a device's model comes from: a parametric family, or an imported file. */
export type DeviceModelSource = 'library' | 'ifc';

/**
 * The families the app can attach to a device. Three of them are the house's
 * shade builders (`bimShadeParts.ts`); the fourth is the LUMINAIRE family
 * (`bimLuminaireParts.ts`, card t_d267b30d) — the fixture a light device is
 * (owner report: *"i have a lamp selested and it gives me shades model here"*).
 */
export type LibraryModelType =
  | 'venetian-blind'
  | 'zip-roller'
  | 'roof-shutter'
  | 'luminaire'
  | 'garage-door'
  | 'sliding-gate'
  | 'swing-gate'
  | 'water-tank'
  | 'waste-tank';

/** The shade families — the ones whose geometry is a `bimShadeParts` build. */
export type ShadeLibraryType = 'venetian-blind' | 'zip-roller' | 'roof-shutter';

/**
 * The ACCESS families — a door and the two gates, built from their own parts
 * module (`bimGarageDoorParts.ts`, `bimGateParts.ts`). A gate is no more a blind
 * than a door is, and they share one class because the hub files them together
 * (`category: 'access'`, controlType `garage_door`).
 */
export type AccessLibraryType = 'garage-door' | 'sliding-gate' | 'swing-gate';

/** Every family the device-model layer can build parts for. */
export type DeviceModelPartsType = ShadeLibraryType | AccessLibraryType;

/** True when a family's geometry is a `bimShadeParts` / `bimGarageDoorParts` build. */
export function isDeviceModelPartsType(type: LibraryModelType): type is DeviceModelPartsType {
  return type === 'venetian-blind' || type === 'zip-roller' || type === 'roof-shutter'
    || type === 'garage-door' || type === 'sliding-gate' || type === 'swing-gate';
}

/**
 * Which DEVICE CLASS a family belongs to. The 3D-model section offers a device
 * the families of its own class — a light is never offered a shade list again,
 * and a cover keeps its own (card t_d267b30d). `tank` is the water cistern
 * (class `water_tank`), whose own family is `water-tank@1`.
 */
export type DeviceModelKind = 'cover' | 'light' | 'access' | 'tank' | 'waste' | 'unknown';

// ─── The RAL palette (devices/palette.py) ────────────────────────────────────

export interface RalTone {
  code: string;
  /** Linear-ish sRGB triplet, the palette's own numbers. */
  rgb: Vec3;
  en: string;
  sl: string;
}

/**
 * The house palette, verbatim from `devices/palette.py`. A device model's colour
 * is stored as the RAL CODE (a string, the house's own language: the IFC styles
 * are named "RAL 7016_PANELI"), and the renderer resolves it here.
 */
export const RAL_TONES: readonly RalTone[] = [
  { code: '7016', rgb: [0.22, 0.24, 0.27], en: 'Anthracite grey', sl: 'Antracit siva' },
  { code: '7015', rgb: [0.2, 0.21, 0.23], en: 'Slate grey', sl: 'Skrilasta siva' },
  { code: '9005', rgb: [0.02, 0.02, 0.02], en: 'Jet black', sl: 'Črna' },
  { code: '9016', rgb: [0.94, 0.945, 0.93], en: 'Traffic white', sl: 'Prometno bela' },
  { code: '7035', rgb: [0.82, 0.83, 0.81], en: 'Light grey', sl: 'Svetlo siva' },
  { code: '8017', rgb: [0.15, 0.1, 0.08], en: 'Chocolate brown', sl: 'Čokoladno rjava' },
];

export const DEFAULT_RAL_CODE = '7016';

/**
 * A RAL name in the palette's language -> the tone, or null.
 *
 * Tolerant on purpose (the palette's own `resolve()`): "7016", "RAL 7016",
 * "RAL 7016_PANELI" and "ral 7016 paneli" all name the anthracite. A colour the
 * palette does not know stays null — the caller then keeps the family default
 * rather than rendering a guess.
 */
export function ralTone(name: unknown): RalTone | null {
  if (typeof name !== 'string') return null;
  const text = name
    .toUpperCase()
    .replace(/RAL/g, ' ')
    .replace(/[_-]/g, ' ')
    .trim();
  const tokens = text.split(/\s+/).filter((token) => token.length > 0);
  for (const tone of RAL_TONES) {
    if (tokens.includes(tone.code) || text === tone.code) return tone;
  }
  return null;
}

/** The RGB triplet a stored RAL code names (unknown -> the house default). */
export function ralRgb(name: unknown): Vec3 {
  return (ralTone(name) ?? RAL_TONES[0]).rgb;
}

// ─── The library ─────────────────────────────────────────────────────────────

/** The parameters the owner can set, per family (the schemas' own `prefs`). */
export interface LibraryPrefs {
  /** RAL code, the slat/fabric tone (a luminaire's body tone, same language). */
  color: string;
  /** Shades: the head box tucks back. The luminaire family does not carry it. */
  cassetteHidden?: boolean;
  /** Jalousie only: the slat stack pitch, mm (schema 40 … 120, default 78). */
  slatPitchMm?: number;
  /** Fabric families only: solar-screen openness %, the fabric's transparency. */
  opennessPct?: number;
  /**
   * Garage doors: the horizontal SECTIONS and the panels across each one
   * (`prefs.rows` / `prefs.cols` in `devices/garage-door/schema.json`). The
   * grid is the family's own shape — the frame's width/height scales it.
   */
  rows?: number;
  cols?: number;
  /** Sliding gates: the telescopic sections (schema 2 … 3, default 2). */
  sections?: number;
  /** Swing gates: 1 (a single pedestrian leaf) or 2 (default). */
  leaves?: number;
  /**
   * LEGACY size overrides. The frame's own size lives on
   * `placement.widthMm` / `placement.dropFullMm` (that is what the editor's
   * fields write and what the renderer builds from); these two are read only as
   * a fallback for a slot stored before that split, and are never written back.
   */
  widthMm?: number;
  dropFullMm?: number;
  /** Luminaires: which fixture the family builds (`schema.json prefs.variant`). */
  variant?: LuminaireVariant;
  /**
   * Luminaires: the MOUNTING SPOT the fixture is seated on (card t_0b96786f) —
   * ceiling · pendant · wallTop · wallBottom · floor. A slot stored before the
   * vocabulary existed carries none, which reads as the variant's own default
   * seat; nothing else about the slot changes.
   */
  mount?: LuminaireMount;
  /** Luminaires: the accent tone — brass / alu / black rings, caps, liners. */
  accent?: LuminaireAccent;
  /**
   * Luminaires: per-variant mm overrides of the module's own defaults
   * (`schema.json prefs.size` — the covers' frame rule does not apply: a
   * fixture anchor is a point, so the family's own size table sizes it).
   */
  sizeMm?: Record<string, number>;
  /**
   * Luminaires: the light's colour TEMPERATURE, K (`prefs.cct`) — the design
   * decision both the glow and the real light take their colour from (owner,
   * 2026-10-18: *"we'll go with tier 1"*). Absent reads as
   * `LUMINAIRE_DEFAULT_CCT` (3000 K), so no stored slot changes shape.
   */
  cct?: number;
  /**
   * Luminaires: the cone the fixture throws, degrees (`prefs.beamDeg`). Absent
   * reads as the variant's own angle (`LUMINAIRE_BEAM_DEG`); a strip, a panel
   * and a globe have no cone and ignore it.
   */
  beamDeg?: number;
  /**
   * Luminaires: may this fixture CAST a shadow (`prefs.shadow`)?
   *
   * The owner's own idea (2026-10-21: *"maybe we could have a per lamp setting which
   * gets the real shadow and which not?"*), and the right one: a GPU can only afford a
   * dozen shadow maps, so WHICH dozen is a design decision, not something to infer from
   * whichever lamp was switched last.
   *
   *   `true`  — always takes one of the shadow slots, if there is one free
   *   `false` — never casts; its light still lights the room, it just ignores walls
   *   absent  — "auto": fills whatever slots the explicit choices leave
   */
  shadow?: boolean;
  /**
   * Luminaires: the target ILLUMINANCE on the floor, lux (`prefs.lux`).
   *
   * Candela is not a number anyone can reason about ("all lights are too bright",
   * owner, 2026-10-21); lux on the floor is what a lighting designer specifies. When set,
   * the fixture's candela is solved from it by inverse square over the height it hangs
   * above its room's floor. Absent = the variant's own table.
   */
  lux?: number;
  /**
   * Luminaires: keep this fixture's light inside its own room (`prefs.clipToRoom`).
   *
   * On by default — a globe in a bathroom reaching 12 m through a wall is not a design
   * decision. Off is the escape hatch for an open plan or a garden fixture whose room
   * section is smaller than the space it is really lighting.
   */
  clipToRoom?: boolean;
  /**
   * Luminaires: which way the cone points (`prefs.aim`).
   *
   * A wall sconce was hard-wired to throw along its wall's normal, so an uplight and a
   * step light could not be modelled (owner, 2026-10-21: *"how about cone up/down/backwards
   * direction for wall lights sconces"*). `out` is the old behaviour — into the room — and
   * `back` turns it toward the wall.
   */
  aim?: 'down' | 'up' | 'out' | 'back';
  /**
   * Water tanks: which cistern the family builds (`prefs.shape`, schema
   * `devices/water-tank/schema.json`). Absent reads as the IBC tote — the shape
   * a site most often has, and the one the module's CLI defaults to.
   */
  shape?: WaterTankShape;
}

export interface LibraryFamily {
  type: LibraryModelType;
  /** The schema's own `model` tag. */
  model: string;
  icon: string;
  titleEn: string;
  titleSl: string;
  /**
   * Which live device values move it (the app's matchers: `stores/shades.ts`,
   * and for a luminaire `stores/lights.ts` + `utils/bimLuminaireSpec.ts`):
   * shades follow position/tilt, a light follows its POWER — it glows.
   */
  follows: 'position+tilt' | 'position' | 'power' | 'level';
  defaults: {
    color: string;
    cassetteHidden?: boolean;
    slatPitchMm?: number;
    opennessPct?: number;
    widthMm?: number;
    dropFullMm?: number;
    /** Luminaires: the variant the family builds when nothing else is known. */
    variant?: LuminaireVariant;
    /** Luminaires: the accent tone the house's records default to (brass). */
    accent?: LuminaireAccent;
    /** Garage doors: the section/panel grid a fresh attach starts from. */
    rows?: number;
    cols?: number;
    /** Sliding gates: how many telescopic sections; swing gates: how many leaves. */
    sections?: number;
    leaves?: number;
    /** Water tanks: the cistern shape a fresh attach starts on. */
    shape?: WaterTankShape;
  };
  limits: {
    slatPitchMm?: { min: number; max: number };
    opennessPct?: { min: number; max: number };
    rows?: { min: number; max: number };
    cols?: { min: number; max: number };
    sections?: { min: number; max: number };
    leaves?: { min: number; max: number };
    widthMm?: { min: number; max: number };
    dropFullMm?: { min: number; max: number };
  };
}

/** A blind's default attach size (mm) when the operator sets no override. */
export const DEVICE_MODEL_DEFAULT_WIDTH_MM = 1400;
export const DEVICE_MODEL_DEFAULT_DROP_MM = 1400;
export const DEVICE_MODEL_MIN_WIDTH_MM = 300;
export const DEVICE_MODEL_MAX_WIDTH_MM = 6000;
export const DEVICE_MODEL_MIN_DROP_MM = 300;
export const DEVICE_MODEL_MAX_DROP_MM = 6000;

const SIZE_LIMITS = {
  widthMm: { min: DEVICE_MODEL_MIN_WIDTH_MM, max: DEVICE_MODEL_MAX_WIDTH_MM },
  dropFullMm: { min: DEVICE_MODEL_MIN_DROP_MM, max: DEVICE_MODEL_MAX_DROP_MM },
};

/**
 * The luminaire family's default body tone, in the house's own language (a RAL
 * code, like every other family's colour). RAL 9005 is the palette's jet black —
 * the app's stand-in for the module's matte black aluminium (`COLOR_BODY`), and
 * the tone the owner asked for: matte black bodies, brass accents.
 */
export const LUMINAIRE_LIBRARY_COLOR = '9005';

/** What the luminaire family's own ui can offer as the body tone. */
export const LUMINAIRE_DEFAULT_ACCENT: LuminaireAccent = 'brass';

/** One size field of a luminaire variant (the editor draws these). */
export interface LuminaireSizeField {
  /** The module's own key in `DEFAULTS`/`size` (`dia`, `drop`, `length`, …). */
  key: string;
  en: string;
  sl: string;
  min: number;
  max: number;
  step: number;
}

/**
 * The per-variant size fields and their ranges — the module's DEFAULTS keys,
 * with a range wide enough for a real fixture of that kind and narrow enough
 * that a typo (a `dia` in metres, a 90 m pole) cannot be stored.
 */
export const LUMINAIRE_SIZE_FIELDS: Record<LuminaireVariant, readonly LuminaireSizeField[]> = {
  spot: [
    { key: 'dia', en: 'Diameter (mm)', sl: 'Premer (mm)', min: 40, max: 600, step: 2 },
    { key: 'height', en: 'Height (mm)', sl: 'Višina (mm)', min: 40, max: 600, step: 2 },
  ],
  pendant: [
    { key: 'shade_dia', en: 'Shade diameter (mm)', sl: 'Premer senčnika (mm)', min: 100, max: 1200, step: 10 },
    { key: 'shade_h', en: 'Shade height (mm)', sl: 'Višina senčnika (mm)', min: 80, max: 800, step: 10 },
    { key: 'drop', en: 'Drop (mm)', sl: 'Spust (mm)', min: 200, max: 3000, step: 10 },
  ],
  wall: [
    { key: 'w', en: 'Width (mm)', sl: 'Širina (mm)', min: 40, max: 800, step: 5 },
    { key: 'h', en: 'Height (mm)', sl: 'Višina (mm)', min: 60, max: 1500, step: 5 },
    { key: 'depth', en: 'Depth (mm)', sl: 'Globina (mm)', min: 20, max: 400, step: 2 },
  ],
  pole: [
    { key: 'h', en: 'Height (mm)', sl: 'Višina (mm)', min: 300, max: 6000, step: 10 },
    { key: 'head_dia', en: 'Head diameter (mm)', sl: 'Premer glave (mm)', min: 40, max: 600, step: 5 },
  ],
  strip: [{ key: 'length', en: 'Length (mm)', sl: 'Dolžina (mm)', min: 100, max: 6000, step: 10 }],
  bulb: [
    { key: 'bulb_dia', en: 'Globe diameter (mm)', sl: 'Premer krogle (mm)', min: 40, max: 600, step: 2 },
    { key: 'drop', en: 'Drop (mm)', sl: 'Spust (mm)', min: 60, max: 3000, step: 5 },
  ],
  panel: [
    { key: 'length', en: 'Length (mm)', sl: 'Dolžina (mm)', min: 100, max: 3000, step: 10 },
    { key: 'width', en: 'Width (mm)', sl: 'Širina (mm)', min: 20, max: 400, step: 5 },
  ],
};

/** Which class each family belongs to — the section's own grouping rule. */
export const DEVICE_MODEL_KIND_BY_FAMILY: Record<LibraryModelType, DeviceModelKind> = {
  'venetian-blind': 'cover',
  'zip-roller': 'cover',
  'roof-shutter': 'cover',
  luminaire: 'light',
  'garage-door': 'access',
  'sliding-gate': 'access',
  'swing-gate': 'access',
  'water-tank': 'tank',
  'waste-tank': 'waste',
};

/** The class a stored family type belongs to. */
export function familyKind(type: LibraryModelType): DeviceModelKind {
  return DEVICE_MODEL_KIND_BY_FAMILY[type];
}

/**
 * The families the library offers a device of this class. A LIGHT gets the
 * luminaire family and nothing else — the owner's report was a lamp being
 * offered the shade list, and a "sane default" that repeats that is no fix.
 * A COVER keeps the shade families. An UNKNOWN class (a slot written before a
 * re-class, a device whose words name neither) is shown every family, so
 * nothing the operator already attached becomes unreachable.
 */
export function libraryFamilies(kind: DeviceModelKind): readonly LibraryFamily[] {
  if (kind === 'cover') return DEVICE_MODEL_LIBRARY.filter((family) => familyKind(family.type) === 'cover');
  if (kind === 'light') return DEVICE_MODEL_LIBRARY.filter((family) => familyKind(family.type) === 'light');
  // An ACCESS device (a garage door, a gate) is offered its OWN family — the
  // owner's own report, mirrored: a door was being shown the shade list.
  if (kind === 'access') return DEVICE_MODEL_LIBRARY.filter((family) => familyKind(family.type) === 'access');
  // …and a WATER TANK its own cistern too (owner, 2026-10-24: *"we need to add
  // water tank as a demo device … IBC, oval vertical, oval horizontal"*).
  if (kind === 'tank') return DEVICE_MODEL_LIBRARY.filter((family) => familyKind(family.type) === 'tank');
  // …and a WASTE WATER TANK its own treatment plant: a cistern stores clean water,
  // a plant processes dirty water — the owner's two demo devices are not one product.
  if (kind === 'waste') return DEVICE_MODEL_LIBRARY.filter((family) => familyKind(family.type) === 'waste');
  return DEVICE_MODEL_LIBRARY;
}

/**
 * The family a fresh "library" attach starts from: a cover starts on the
 * jalousie (the house's most common cover, and what the section has always
 * seeded), a light starts on the LUMINAIRE — never on a shade.
 */
export function defaultLibraryType(kind: DeviceModelKind): LibraryModelType {
  if (kind === 'light') return 'luminaire';
  if (kind === 'access') return 'garage-door';
  if (kind === 'tank') return 'water-tank';
  if (kind === 'waste') return 'waste-tank';
  return 'venetian-blind';
}

/**
 * The families the app can attach to a device. The defaults are the schemas' own
 * (`devices/venetian-blind/schema.json`, `zip-roller`, `roof-shutter`,
 * `luminaire`); the luminaire's GEOMETRY is `bimLuminaireParts.ts` (the port of
 * `devices/luminaire/luminaire.py`, card t_d267b30d), and its live behaviour is
 * the glow the live layer already gives a baked twin (`bimLuminaireSpec.ts`).
 */
export const DEVICE_MODEL_LIBRARY: readonly LibraryFamily[] = [
  {
    type: 'venetian-blind',
    model: 'venetian-blind@1',
    icon: 'blinds',
    titleEn: 'External venetian blind',
    titleSl: 'Zunanja žaluzija',
    follows: 'position+tilt',
    defaults: {
      color: DEFAULT_RAL_CODE,
      cassetteHidden: true,
      slatPitchMm: 78,
      opennessPct: 3,
      widthMm: DEVICE_MODEL_DEFAULT_WIDTH_MM,
      dropFullMm: DEVICE_MODEL_DEFAULT_DROP_MM,
    },
    limits: { slatPitchMm: { min: 40, max: 120 }, opennessPct: { min: 0, max: 30 }, ...SIZE_LIMITS },
  },
  {
    type: 'zip-roller',
    model: 'zip-roller@1',
    icon: 'roller_shades',
    titleEn: 'Exterior zip roller screen',
    titleSl: 'Zunanji zip rolo',
    follows: 'position',
    defaults: {
      color: DEFAULT_RAL_CODE,
      cassetteHidden: true,
      slatPitchMm: 78,
      opennessPct: 3,
      widthMm: DEVICE_MODEL_DEFAULT_WIDTH_MM,
      dropFullMm: DEVICE_MODEL_DEFAULT_DROP_MM,
    },
    limits: { slatPitchMm: { min: 40, max: 120 }, opennessPct: { min: 0, max: 30 }, ...SIZE_LIMITS },
  },
  {
    type: 'roof-shutter',
    model: 'roof-shutter@1',
    icon: 'window',
    titleEn: 'Roof-window roller shutter',
    titleSl: 'Roletna za strešno okno',
    follows: 'position',
    defaults: {
      color: DEFAULT_RAL_CODE,
      cassetteHidden: true,
      slatPitchMm: 78,
      opennessPct: 3,
      widthMm: DEVICE_MODEL_DEFAULT_WIDTH_MM,
      dropFullMm: DEVICE_MODEL_DEFAULT_DROP_MM,
    },
    limits: { slatPitchMm: { min: 40, max: 120 }, opennessPct: { min: 0, max: 30 }, ...SIZE_LIMITS },
  },
  {
    // The owner's lamp ("i have a lamp selested and it gives me shades model
    // here"): the house's own luminaire family — matte black aluminium body,
    // brass accents, opal diffuser — sized by its own per-variant table (a
    // fixture anchor is a point, so the covers' frame fields do not apply) and
    // moved by the light's POWER, not by a position (card t_d267b30d).
    type: 'luminaire',
    model: 'luminaire@1',
    icon: 'lightbulb',
    titleEn: 'Luminaire (light fixture)',
    titleSl: 'Svetilo',
    follows: 'power',
    defaults: {
      color: LUMINAIRE_LIBRARY_COLOR,
      variant: 'spot',
      accent: 'brass',
    },
    limits: {},
  },
  {
    // The house's garage door (owner, 2026-10-16: *"add garage door to
    // library!"*): the sectioned leaf the canvas' `NV4` element was, as a
    // parametric family — so a `garage_door` device is offered ITS OWN model
    // instead of the shade list it used to fall back to. The module is
    // `devices/garage-door/garage_door.py`; `bimGarageDoorParts.ts` is the port
    // and `follows: 'position'` is the same live read a cover gets (the hub
    // reports a door's `position` as 0 = open … 100 = closed, which is exactly
    // the drop the parts are built at).
    type: 'garage-door',
    model: 'garage-door@1',
    icon: 'garage',
    titleEn: 'Sectional garage door',
    titleSl: 'Sekcijska garažna vrata',
    follows: 'position',
    defaults: {
      color: DEFAULT_RAL_CODE,
      rows: GARAGE_DOOR.rows,
      cols: GARAGE_DOOR.cols,
      widthMm: 3000,
      dropFullMm: 2250,
    },
    limits: {
      rows: { min: GARAGE_DOOR.minRows, max: GARAGE_DOOR.maxRows },
      cols: { min: GARAGE_DOOR.minCols, max: GARAGE_DOOR.maxCols },
      ...SIZE_LIMITS,
    },
  },
  {
    // The driveway gates (owner, 2026-10-16: *"we need a model for gates. same
    // as garage doors, but different if models.. ... could we replace it by 2
    // section (telescopic) sliding gates.. we also need swing gates (2)"*). Both
    // are `access` and both follow the device's `position`; the module is
    // `devices/sliding-gate/sliding_gate.py` and `bimGateParts.ts` is the port.
    type: 'sliding-gate',
    model: 'sliding-gate@1',
    icon: 'fence',
    titleEn: 'Telescopic sliding gate',
    titleSl: 'Teleskopska drsna vrata',
    follows: 'position',
    defaults: {
      color: DEFAULT_RAL_CODE,
      sections: SLIDING_GATE.sections,
      widthMm: 4000,
      dropFullMm: 1800,
    },
    limits: {
      sections: { min: SLIDING_GATE.minSections, max: SLIDING_GATE.maxSections },
      ...SIZE_LIMITS,
    },
  },
  {
    type: 'swing-gate',
    model: 'swing-gate@1',
    icon: 'fence',
    titleEn: 'Swing gate (double)',
    titleSl: 'Nihajna vrata (dvokrilna)',
    follows: 'position',
    defaults: {
      color: DEFAULT_RAL_CODE,
      leaves: SWING_GATE.leaves,
      widthMm: 3500,
      dropFullMm: 1800,
    },
    limits: {
      leaves: { min: SWING_GATE.minLeaves, max: SWING_GATE.maxLeaves },
      ...SIZE_LIMITS,
    },
  },
  {
    // The water cistern (owner, 2026-10-24: *"we need to add water tank as a
    // demo device … ifc models with value updates IBC, oval vertical, oval
    // horizontal.. the walls of the cistern would be slightly transparent.
    // inside there would be water. water level should be visible through the
    // walls"*). The module is `devices/water-tank/water_tank.py`;
    // `bimWaterTankParts.ts` is the port. It follows the tank's own
    // `level_percentage` — the hub water_tank class's required value — and its
    // shell is drawn translucent so the live water line reads from outside.
    type: 'water-tank',
    model: 'water-tank@1',
    icon: 'water_full',
    titleEn: 'Water cistern (tank)',
    titleSl: 'Vodni zbiralnik (cisterna)',
    follows: 'level',
    defaults: {
      color: '7035',
      shape: 'ibc',
    },
    limits: {},
  },
  {
    // The waste-water treatment plant (owner, 2026-10-24: *"add waste water
    // processing tank ('biološka čistilna naprava').. hirizontal tank"*): a
    // horizontal biological plant in three chambers, its own family and its own
    // device class (`waste_tank`). Geometry: `devices/waste-tank/waste_tank.py`
    // → `bimWasteTankParts.ts`; it follows the plant's fill level like the
    // cistern does, with the chambers drawn stepped off that one reading.
    type: 'waste-tank',
    model: 'waste-tank@1',
    icon: 'water_damage',
    titleEn: 'Waste water treatment plant',
    titleSl: 'Čistilna naprava za odpadno vodo',
    follows: 'level',
    defaults: { color: '7035' },
    limits: {},
  },
];

export function libraryFamily(type: unknown): LibraryFamily | null {
  if (typeof type !== 'string') return null;
  return DEVICE_MODEL_LIBRARY.find((family) => family.type === type) ?? null;
}

/**
 * The family's own default params, on a fresh object.
 *
 * `seed.variant` lets a caller open a LUMINAIRE on the variant the device's own
 * name classifies to (the pipeline's `variant_for`, mirrored in
 * `bimLuminaireSpec.ts`) — the fixture the house itself would have built for
 * that lamp. It is only a SEED: the operator's own pick is stored in the prefs
 * and wins from then on.
 */
export function defaultLibraryPrefs(
  type: LibraryModelType,
  seed: { variant?: LuminaireVariant } = {},
): LibraryPrefs {
  const family = libraryFamily(type) ?? DEVICE_MODEL_LIBRARY[0];
  if (family.type === 'luminaire') {
    const variant = seed.variant ?? family.defaults.variant ?? 'spot';
    return {
      color: family.defaults.color,
      variant,
      // The mount a fresh attach starts on is the variant's own seat
      // (spot → ceiling, pendant → pendant, wall → wallTop, pole → floor).
      mount: mountForVariant(variant),
      accent: family.defaults.accent ?? LUMINAIRE_DEFAULT_ACCENT,
    };
  }
  if (family.type === 'garage-door') {
    return {
      color: family.defaults.color,
      rows: family.defaults.rows ?? GARAGE_DOOR.rows,
      cols: family.defaults.cols ?? GARAGE_DOOR.cols,
    };
  }
  if (family.type === 'sliding-gate') {
    return { color: family.defaults.color, sections: family.defaults.sections ?? SLIDING_GATE.sections };
  }
  if (family.type === 'swing-gate') {
    return { color: family.defaults.color, leaves: family.defaults.leaves ?? SWING_GATE.leaves };
  }
  if (family.type === 'water-tank') {
    // A tank has no frame size and no slats: its wall tone and its SHAPE are its
    // own params (the level is live, and never a pref — it belongs to the hub).
    return { color: family.defaults.color, shape: family.defaults.shape ?? 'ibc' };
  }
  if (family.type === 'waste-tank') {
    // The treatment plant has ONE shape: its wall tone is its only pref.
    return { color: family.defaults.color };
  }
  return {
    color: family.defaults.color,
    cassetteHidden: family.defaults.cassetteHidden ?? true,
    ...(family.type === 'venetian-blind'
      ? { slatPitchMm: family.defaults.slatPitchMm ?? 78 }
      : { opennessPct: family.defaults.opennessPct ?? 3 }),
    // No size here: the frame (`placement`) owns width/drop — seeding them as
    // prefs made every placement edit snap back on the next parse (a stored
    // prefs size used to win over the placement it was written beside).
  };
}

/**
 * The stored size overrides of a luminaire slot, filtered to the variant's own
 * keys and clamped into the field's range (the schema's `prefs.size` rule: an
 * override for another variant's key is not a value). `undefined` when the
 * operator overrode nothing — the module's DEFAULTS are then the size.
 */
export function readLuminaireSize(variant: LuminaireVariant, raw: unknown): Record<string, number> | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Record<string, number> = {};
  for (const field of LUMINAIRE_SIZE_FIELDS[variant]) {
    const value = raw[field.key];
    if (typeof value === 'number' && Number.isFinite(value)) out[field.key] = clampNumber(value, field.min, field.max);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** A stored param, clamped into the family's own limits (the schema's range). */
function readParamNumber(value: unknown, limit: { min: number; max: number }, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return clampNumber(value, limit.min, limit.max);
}

// ─── The stored slot ─────────────────────────────────────────────────────────

/** The frame a model is rendered in: a point, a facing, and its own size. */
export interface DeviceModelPlacement {
  /** The widget's anchor, scene/IFC metres. */
  origin: Vec3;
  /** Facing about the world up axis (degrees): 0 = width along +X, out of plane +Z. */
  yawDeg: number;
  /**
   * The MOUNTING PLANE's own tilt, degrees (card t_ad566d07): 0 = a vertical
   * wall — the default, and every slot stored before this field existed.
   * Positive leans the plane back so its outward normal tips up out of
   * horizontal and the frame's `up` runs up-slope: a 40.3° roof slope like this
   * house's is `90 − 40.3 = 49.7` (the normal sits 49.7° above the horizon).
   * For a unit surface normal `n` that is `asin(n.y)`; the width axis does not
   * move with it.
   */
  pitchDeg: number;
  widthMm: number;
  dropFullMm: number;
}

export interface DeviceLibrarySelection {
  type: LibraryModelType;
  model: string;
  prefs: LibraryPrefs;
}

export interface DeviceIfcSelection {
  /** The hub path the bytes are fetched from (`/api/...`, transport-agnostic). */
  url: string;
  name: string;
  bytes: number;
  uploadedAt: string;
  /** Optional size multiplier the operator set for the imported file. */
  scale: number;
}

export interface DeviceModel3d {
  source: DeviceModelSource;
  library: DeviceLibrarySelection | null;
  ifc: DeviceIfcSelection | null;
  placement: DeviceModelPlacement;
  /** The device whose values drive it; null = the widget's own `id` (cfg.id). */
  driverId: string | null;
}

const AXIS_U_YAW0: Vec3 = [1, 0, 0];
const AXIS_OUT_YAW0: Vec3 = [0, 0, 1];
const AXIS_UP: Vec3 = [0, 1, 0];

/** The pitch's own range: ±90 tips the plane all the way to face straight up / down. */
const PITCH_LIMITS = { min: -90, max: 90 };

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/** A vector turned by `deg` about the world up axis (the yaw, +Y up). */
function yawed(base: Vec3, cos: number, sin: number): Vec3 {
  return [cos * base[0] + sin * base[2], base[1], -sin * base[0] + cos * base[2]];
}

/** An angle folded into (−180, 180]. */
export function normalizeTurnDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let value = ((deg % 360) + 360) % 360;
  if (value > 180) value -= 360;
  return value;
}

// ─── The ELEMENT's own mounting plane (card t_9a056ea5) ─────────────────────

/**
 * The mounting plane the ELEMENT under a widget actually has, read off the
 * widget's own anchor — the outward normal the app already binds, re-seats and
 * re-derives on every model load (`utils/bimObjects.bimAnchorOf`:
 * `bimAnchor.normal`, with the older `normalX/Y/Z` triple as the fallback).
 *
 * Card t_9a056ea5 rewired the shade frame onto this plane, and the reason is
 * measured, not theoretical: `placement.yawDeg` was seeded from the record
 * layer's own outward normal in the frame of THAT day
 * (`scripts/attach-demo-device-models-t_14be3874.ts` → `atan2(n.x, n.z)`), and
 * when the served model was later axis-aligned (card t_3b4e6e9a:
 * `TorikDemoHouse-v2.5-site-aligned.align.json`, **+90.002° about Y**) only the
 * widget pins/anchors were rotated — every stored slot kept its pre-alignment
 * yaw, 90° out in the new frame, so the blind's WIDTH axis landed on the
 * window's real normal and the slats fanned straight out of the façade:
 *
 *     one device-level slot, two dashboards, two model frames → one number
 *     cannot be right in both; the element's own normal is.
 *
 * So the plane comes from the element (per page, per widget — it re-seats on
 * load) and `placement.yawDeg` / `pitchDeg` are read as the operator's TURN and
 * extra LEAN measured FROM that plane: 0 is flat in the window, which is what
 * "attach a shade to this window" has to mean.
 */
export interface MountingPlane {
  /** Unit outward normal, scene/IFC metres. */
  normal: Vec3;
  /** The plane's own facing about the vertical — `atan2(n.x, n.z)`, degrees. */
  yawDeg: number;
  /** The plane's own lean from vertical — `asin(n.y)`, degrees (0 = a wall). */
  leanDeg: number;
}

/**
 * How much of a plane's normal must lie in the horizontal plane for it to name
 * a MOUNTING PLANE at all: a wall or a roof slope has a width direction along
 * the wall; a ceiling or a floor does not (its normal is the world up), and a
 * shade on one has no window to be flat in. Below this share the caller keeps
 * the absolute frame it had before this card.
 */
export const MIN_PLANE_HORIZONTAL = 0.2;

/** The element's mounting plane, or null when the widget carries no usable one. */
export function mountingPlane(config: unknown): MountingPlane | null {
  if (!isRecord(config)) return null;
  const anchor = isRecord(config.bimAnchor) ? config.bimAnchor : null;
  const raw = anchor?.normal ?? [config.normalX, config.normalY, config.normalZ];
  const normal = readVec3(raw);
  if (!normal) return null;
  const length = Math.hypot(normal[0], normal[1], normal[2]);
  if (length < 1e-6) return null;
  const unit: Vec3 = [normal[0] / length, normal[1] / length, normal[2] / length];
  const horizontal = Math.hypot(unit[0], unit[2]);
  if (horizontal < MIN_PLANE_HORIZONTAL) return null; // a ceiling / a floor: no width along a wall
  return {
    normal: unit,
    yawDeg: (Math.atan2(unit[0], unit[2]) * 180) / Math.PI,
    leanDeg: (Math.asin(Math.max(-1, Math.min(1, unit[1]))) * 180) / Math.PI,
  };
}

/** The plane's own facing as the stored turn a shade carries on it. */
export function planeTurnDeg(plane: MountingPlane | null, worldYawDeg: number): number {
  return normalizeTurnDeg(worldYawDeg - (plane?.yawDeg ?? 0));
}

/** The world facing a stored turn stands for — the angle a rotate ring sits at. */
export function planeWorldYawDeg(plane: MountingPlane | null, turnDeg: number): number {
  return normalizeTurnDeg((plane?.yawDeg ?? 0) + turnDeg);
}

/**
 * The plane a slot's stored turn/lean are read FROM — the ELEMENT's plane for a
 * SHADE family, whose geometry is authored flat in a mounting plane, and `null`
 * for a luminaire and an imported file: a fixture's own frame is the one its
 * geometry is drawn in (`+Z` up in `bimLuminaireParts`, the file's own axes for
 * an import), so their yaw stays the absolute facing it has always been (card
 * t_9a056ea5). The one rule lives here so the renderer, the gizmo and the editor
 * can never disagree about whose frame a number is in.
 */
export function placementPlane(model: DeviceModel3d, plane: MountingPlane | null): MountingPlane | null {
  if (model.source !== 'library' || !model.library || model.library.type === 'luminaire') return null;
  // …and the tank stands on the FLOOR: its frame is world-up (the module's own
  // +Z), so a pin re-seated on a surface normal must not tip it on its side.
  if (model.library.type === 'water-tank' || model.library.type === 'waste-tank') return null;
  return plane;
}

/**
 * The placement's frame as three unit axes in the scene frame — the frame a
 * device's own model is BUILT in (card t_ad566d07):
 *
 *   · `u`   — along the mounting plane's width (a window's width);
 *   · `up`  — up the plane (a window's height direction);
 *   · `out` — the plane's outward normal.
 *
 * A yaw of 0 with a pitch of 0 is the upright frame: width along +X, out along
 * +Z, up along +Y. The yaw turns the whole frame about the world up axis; the
 * pitch then tips `out` up out of horizontal by that many degrees (about `u`),
 * with `up` following in step — a roof window's frame leans back like the roof
 * it sits on, and its `up` runs up-slope. Pitching changes which way the shade
 * drops; it does not move the anchor.
 *
 * With a `plane` in hand (card t_9a056ea5) the placement's yaw and pitch are
 * read FROM that plane: the plane's own facing and lean are the base, and the
 * two stored numbers are the operator's turn and extra lean on top of it —
 * `u = cross(up, normal)`, i.e. along the wall, at rest. Without a plane the
 * two numbers are the absolute frame they have always been (every slot, and
 * every widget that carries no anchor, renders exactly as before this card).
 *
 * Unpitched, the three axes are the same roles `bimShadeSpec.ts` spells
 * `axisU/V/W` for the hub-authored twins, so both layers place a shade alike.
 */
export function placementAxes(
  placement: DeviceModelPlacement,
  plane: MountingPlane | null = null,
): { u: Vec3; up: Vec3; out: Vec3 } {
  const turn = Number.isFinite(placement.yawDeg) ? placement.yawDeg : 0;
  const lean = Number.isFinite(placement.pitchDeg) ? placement.pitchDeg : 0;
  const yaw = (turn + (plane?.yawDeg ?? 0)) * (Math.PI / 180);
  const pitch = (lean + (plane?.leanDeg ?? 0)) * (Math.PI / 180);
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  const u = yawed(AXIS_U_YAW0, cosYaw, sinYaw);
  const horizontal = yawed(AXIS_OUT_YAW0, cosYaw, sinYaw);
  const cosPitch = Math.cos(pitch);
  const sinPitch = Math.sin(pitch);
  const out: Vec3 = [
    horizontal[0] * cosPitch + AXIS_UP[0] * sinPitch,
    horizontal[1] * cosPitch + AXIS_UP[1] * sinPitch,
    horizontal[2] * cosPitch + AXIS_UP[2] * sinPitch,
  ];
  // Right-handed: u × up = out, so up = out × u.
  const up = cross(out, u);
  return { u, up, out };
}

/**
 * Which ring of the rotate gizmo a drag is on (card t_b27982a1) — the
 * placement's own two degrees of freedom, and deliberately nothing else:
 *
 *   · `turn` — the VERTICAL ring, about the world up axis: the object's facing;
 *   · `lean` — the WIDTH ring, about the frame's own horizontal width axis
 *     (`placementAxes().u`, which a pitch does not move): the operator's lean
 *     off the mounting plane, the roof shutters' own variant.
 *
 * A placement stores a turn and a lean (`DeviceModelPlacement`), so three's roll
 * ring and its free-rotation rings ('E', 'XYZE') are not offered: a ring that
 * turns about anything else could only be honoured by throwing a component away.
 */
export type DeviceModelRing = 'turn' | 'lean';

/**
 * The placement one RING DRAG leaves behind: the ring turns the object about its
 * OWN axis by `stepDeg` (degrees, right-handed, in the world frame), and that
 * displacement folds into the one stored number the ring owns.
 *
 * Exact by construction. The turn adds to the stored turn (a world-yaw step is
 * the stored turn's own step — the element's plane is fixed under the drag), and
 * the lean subtracts from the stored pitch, because `placementAxes` builds the
 * frame as `Ry(yaw) · Rx(−pitch)`: a right-handed quarter-turn about the width
 * axis `u` by θ lands on the same frame with `pitch − θ`. No angle is ever
 * re-derived from the composed orientation — an Euler extraction is exactly what
 * made a vertical drag write a mirrored facing and stall at 90°.
 *
 * A lean is bounded by the storage's own ±90 (a plane facing straight up/down
 * is the extreme), so a drag past it clamps there rather than storing a frame
 * nothing can render.
 */
export function ringDragPlacement(
  placement: DeviceModelPlacement,
  ring: DeviceModelRing,
  stepDeg: number,
): DeviceModelPlacement {
  if (!Number.isFinite(stepDeg) || stepDeg === 0) return placement;
  if (ring === 'turn') {
    return { ...placement, yawDeg: normalizeTurnDeg(placement.yawDeg + stepDeg) };
  }
  const lean = (Number.isFinite(placement.pitchDeg) ? placement.pitchDeg : 0) - stepDeg;
  return { ...placement, pitchDeg: clampNumber(lean, PITCH_LIMITS.min, PITCH_LIMITS.max) };
}

/** A placement at a point, with the family's defaults. */
export function defaultPlacement(
  origin: Vec3,
  overrides: Partial<Omit<DeviceModelPlacement, 'origin'>> = {},
): DeviceModelPlacement {
  return {
    origin: [origin[0], origin[1], origin[2]],
    yawDeg: readParamNumber(overrides.yawDeg, { min: -360, max: 360 }, 0),
    pitchDeg: readParamNumber(overrides.pitchDeg, PITCH_LIMITS, 0),
    widthMm: readParamNumber(overrides.widthMm, SIZE_LIMITS.widthMm, DEVICE_MODEL_DEFAULT_WIDTH_MM),
    dropFullMm: readParamNumber(overrides.dropFullMm, SIZE_LIMITS.dropFullMm, DEVICE_MODEL_DEFAULT_DROP_MM),
  };
}

/** The widget's own anchor (`x3d/y3d/z3d`), or null while it is unplaced. */
export function widgetAnchor(config: unknown): Vec3 | null {
  if (typeof config !== 'object' || config === null) return null;
  const record = config as Record<string, unknown>;
  const x = record.x3d;
  const y = record.y3d;
  const z = record.z3d;
  if (typeof x !== 'number' || !Number.isFinite(x)) return null;
  if (typeof y !== 'number' || !Number.isFinite(y)) return null;
  const zz = typeof z === 'number' && Number.isFinite(z) ? z : 0;
  return [x, y, zz];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readVec3(value: unknown): Vec3 | null {
  if (!Array.isArray(value) || value.length !== 3) return null;
  const out = value.map((entry) => (typeof entry === 'number' && Number.isFinite(entry) ? entry : null));
  if (out.some((entry) => entry === null)) return null;
  return [out[0] as number, out[1] as number, out[2] as number];
}

function readPositive(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * The key a stored slot lives under — the SAME one on both surfaces: a widget's
 * `widgetConfig.model3d` and a device's own `settings.model3d` (card t_6b2c5f01).
 * One key, one shape, one parser; a device's settings bag keeps carrying its own
 * real settings beside it, so the key is never a device setting and the device
 * editor's settings list skips it.
 */
export const DEVICE_MODEL_KEY = 'model3d';

/**
 * A widget config's stored device model, or null when it carries none.
 *
 * Total by contract — a widget config is hub data that any hand-edited row or
 * older build may have written, so a malformed slot reads as "no model" instead
 * of throwing inside a renderer (a throw in a composable takes the whole view
 * down, the failure mode `cloud-payload-field-renames` documents).
 *
 * The same call reads a DEVICE's own slot off its settings bag: the stored shape
 * and its key are identical on both surfaces (`DEVICE_MODEL_KEY`).
 */
export function parseDeviceModel(config: unknown): DeviceModel3d | null {
  if (!isRecord(config)) return null;
  const raw = config[DEVICE_MODEL_KEY];
  if (!isRecord(raw)) return null;
  const source = raw.source === 'ifc' ? 'ifc' : raw.source === 'library' ? 'library' : null;
  if (!source) return null;

  const placementRaw = isRecord(raw.placement) ? raw.placement : {};
  const origin = readVec3(placementRaw.origin) ?? widgetAnchor(config);
  const widthFallback = readPositive(placementRaw.widthMm, DEVICE_MODEL_DEFAULT_WIDTH_MM);
  const dropFallback = readPositive(placementRaw.dropFullMm, DEVICE_MODEL_DEFAULT_DROP_MM);
  const yawDeg =
    typeof placementRaw.yawDeg === 'number' && Number.isFinite(placementRaw.yawDeg) ? placementRaw.yawDeg : 0;
  // A slot written before the field existed is a vertical wall, which is what
  // pitch 0 means — the read is total, so no stored slot changes shape.
  const pitchDeg = readParamNumber(placementRaw.pitchDeg, PITCH_LIMITS, 0);

  const driverId = typeof raw.driverId === 'string' && raw.driverId.length > 0 ? raw.driverId : null;

  if (source === 'library') {
    const libraryRaw = isRecord(raw.library) ? raw.library : {};
    const family = libraryFamily(libraryRaw.type);
    if (!family) return null; // an unknown family has no geometry to build
    const prefsRaw = isRecord(libraryRaw.prefs) ? libraryRaw.prefs : {};
    const color = ralTone(prefsRaw.color)?.code ?? family.defaults.color;
    let prefs: LibraryPrefs;
    if (family.type === 'luminaire') {
      // A luminaire's params are its OWN (the schema's `prefs`): which fixture,
      // its accent tone and its per-variant size overrides. It has no frame
      // fields — a fixture anchor is a point (schema.json's own note).
      const variant = isLuminaireVariant(prefsRaw.variant) ? prefsRaw.variant : family.defaults.variant ?? 'spot';
      const sizeMm = readLuminaireSize(variant, prefsRaw.sizeMm);
      prefs = {
        color,
        variant,
        // The mount is read strictly too: an older slot carries none (it keeps
        // reading as the variant's own default seat) and a word this build does
        // not know is no mount rather than a guess at one.
        ...(isLuminaireMount(prefsRaw.mount) ? { mount: prefsRaw.mount } : {}),
        accent: isLuminaireAccent(prefsRaw.accent) ? prefsRaw.accent : family.defaults.accent ?? LUMINAIRE_DEFAULT_ACCENT,
        ...(sizeMm ? { sizeMm } : {}),
        // The light's own design pair. Absent stays absent (the reader falls back
        // to the house CCT and the variant's beam); present is clamped, so a typo
        // (a 65000 K slot, a 3° cone) cannot be stored by a stray write.
        ...(typeof prefsRaw.cct === 'number' ? { cct: clampCct(prefsRaw.cct) } : {}),
        ...(typeof prefsRaw.beamDeg === 'number'
          ? { beamDeg: Math.round(Math.min(BEAM_LIMITS.max, Math.max(BEAM_LIMITS.min, prefsRaw.beamDeg))) }
          : {}),
        // May this fixture cast a shadow (owner, 2026-10-21)? A strict boolean or
        // nothing: this READER is a whitelist, and a key it does not name is silently
        // dropped on every parse — which is exactly why the new control snapped back to
        // "Auto" a moment after it was set. Storing `shadow` here is what makes the
        // operator's choice survive its own round-trip.
        ...(typeof prefsRaw.shadow === 'boolean' ? { shadow: prefsRaw.shadow } : {}),
        ...(typeof prefsRaw.lux === 'number' && Number.isFinite(prefsRaw.lux)
          ? { lux: Math.max(1, Math.min(4000, prefsRaw.lux)) }
          : {}),
        ...(typeof prefsRaw.clipToRoom === 'boolean' ? { clipToRoom: prefsRaw.clipToRoom } : {}),
        ...(prefsRaw.aim === 'down' || prefsRaw.aim === 'up' || prefsRaw.aim === 'out' || prefsRaw.aim === 'back'
          ? { aim: prefsRaw.aim }
          : {}),
      };
    } else if (family.type === 'sliding-gate' || family.type === 'swing-gate') {
      // The gates' own param: how many telescopic sections, or how many leaves.
      // Both are WHOLE units (half a leaf is not a leaf) and clamped into the
      // schema's range.
      const isSliding = family.type === 'sliding-gate';
      const limit = (isSliding ? family.limits.sections : family.limits.leaves) ?? { min: 1, max: 3 };
      const fallback = (isSliding ? family.defaults.sections : family.defaults.leaves) ?? 2;
      prefs = isSliding
        ? { color, sections: Math.round(readParamNumber(prefsRaw.sections, limit, fallback)) }
        : { color, leaves: Math.round(readParamNumber(prefsRaw.leaves, limit, fallback)) };
    } else if (family.type === 'garage-door') {
      // A door's own params: the section/panel grid, read as WHOLE sections
      // (`Math.round` — half a section is not a section) and clamped into the
      // schema's range.
      const rowsLimit = family.limits.rows ?? { min: GARAGE_DOOR.minRows, max: GARAGE_DOOR.maxRows };
      const colsLimit = family.limits.cols ?? { min: GARAGE_DOOR.minCols, max: GARAGE_DOOR.maxCols };
      prefs = {
        color,
        rows: Math.round(readParamNumber(prefsRaw.rows, rowsLimit, family.defaults.rows ?? GARAGE_DOOR.rows)),
        cols: Math.round(readParamNumber(prefsRaw.cols, colsLimit, family.defaults.cols ?? GARAGE_DOOR.cols)),
      };
    } else if (family.type === 'waste-tank') {
      // The plant's own params: none beyond its wall tone (the level is the
      // device's, the chambers are geometry).
      prefs = { color };
    } else if (family.type === 'water-tank') {
      // A cistern's own param: WHICH tank (the schema's `prefs.shape`). This
      // reader is a whitelist, so a key it does not name is dropped on every
      // parse — the shape select snapped straight back to the IBC until this
      // branch existed (measured live: the panel wrote `oval-vertical`, the
      // view kept drawing the tote).
      prefs = { color, shape: waterTankShape(prefsRaw.shape) ?? family.defaults.shape ?? 'ibc' };
    } else {
      const pitchLimit = family.limits.slatPitchMm ?? { min: 40, max: 120 };
      const opennessLimit = family.limits.opennessPct ?? { min: 0, max: 30 };
      prefs = {
        color,
        cassetteHidden: prefsRaw.cassetteHidden !== false,
        ...(family.type === 'venetian-blind'
          ? { slatPitchMm: readParamNumber(prefsRaw.slatPitchMm, pitchLimit, family.defaults.slatPitchMm ?? 78) }
          : { opennessPct: readParamNumber(prefsRaw.opennessPct, opennessLimit, family.defaults.opennessPct ?? 3) }),
      };
    }
    if (!origin) return null; // nowhere to put it — the operator has not dropped the pin
    // The FRAME owns the size: the stored placement first, an older slot's
    // `prefs.widthMm/dropFullMm` as the fallback, the family default last —
    // clamped into the schema's own range either way. (Reading the prefs first
    // let a prefs size win over the placement it was written beside, so an edit
    // made in the editor's own size fields was reverted by the next re-seed
    // from the stored config — measured on the live hub, the fields snapped
    // back to the family default on the same render.) A luminaire does not use
    // the frame's size — it keeps the values so the slot's shape stays one
    // shape on every surface — so its stored fallbacks are simply carried.
    const frameWidth = readParamNumber(
      placementRaw.widthMm,
      family.limits.widthMm ?? SIZE_LIMITS.widthMm,
      readParamNumber(prefsRaw.widthMm, family.limits.widthMm ?? SIZE_LIMITS.widthMm, family.defaults.widthMm ?? widthFallback),
    );
    const frameDrop = readParamNumber(
      placementRaw.dropFullMm,
      family.limits.dropFullMm ?? SIZE_LIMITS.dropFullMm,
      readParamNumber(prefsRaw.dropFullMm, family.limits.dropFullMm ?? SIZE_LIMITS.dropFullMm, family.defaults.dropFullMm ?? dropFallback),
    );
    return {
      source: 'library',
      library: { type: family.type, model: family.model, prefs },
      ifc: null,
      placement: defaultPlacement(origin, { yawDeg, pitchDeg, widthMm: frameWidth, dropFullMm: frameDrop }),
      driverId,
    };
  }

  const ifcRaw = isRecord(raw.ifc) ? raw.ifc : {};
  const url = typeof ifcRaw.url === 'string' && ifcRaw.url.length > 0 ? ifcRaw.url : null;
  if (!url) return null; // no bytes to render
  if (!origin) return null; // nowhere to put it — the operator has not dropped the pin
  return {
    source: 'ifc',
    library: null,
    ifc: {
      url,
      name: typeof ifcRaw.name === 'string' ? ifcRaw.name : url.split('/').pop() || url,
      bytes: typeof ifcRaw.bytes === 'number' && Number.isFinite(ifcRaw.bytes) ? ifcRaw.bytes : 0,
      uploadedAt: typeof ifcRaw.uploadedAt === 'string' ? ifcRaw.uploadedAt : '',
      scale: readPositive(ifcRaw.scale, 1),
    },
    placement: defaultPlacement(origin, { yawDeg, pitchDeg, widthMm: widthFallback, dropFullMm: dropFallback }),
    driverId,
  };
}

/** The slot as the JSON the hub stores (every field a plain JSON value). */
export function deviceModelJson(model: DeviceModel3d): JsonRecord {
  const placement: JsonRecord = {
    origin: [...model.placement.origin],
    yawDeg: model.placement.yawDeg,
    pitchDeg: model.placement.pitchDeg,
    widthMm: model.placement.widthMm,
    dropFullMm: model.placement.dropFullMm,
  };
  const out: JsonRecord = { source: model.source, placement };
  if (model.driverId) out.driverId = model.driverId;
  if (model.library) {
    const prefs: JsonRecord = { color: model.library.prefs.color };
    if (model.library.prefs.cassetteHidden !== undefined) prefs.cassetteHidden = model.library.prefs.cassetteHidden;
    if (model.library.prefs.slatPitchMm !== undefined) prefs.slatPitchMm = model.library.prefs.slatPitchMm;
    if (model.library.prefs.opennessPct !== undefined) prefs.opennessPct = model.library.prefs.opennessPct;
    if (model.library.prefs.rows !== undefined) prefs.rows = model.library.prefs.rows;
    if (model.library.prefs.cols !== undefined) prefs.cols = model.library.prefs.cols;
    if (model.library.prefs.sections !== undefined) prefs.sections = model.library.prefs.sections;
    if (model.library.prefs.leaves !== undefined) prefs.leaves = model.library.prefs.leaves;
    // A cistern's shape travels out the same way it comes in (the WRITER is a
    // whitelist too — the reader alone does not make a param survive an edit).
    if (model.library.prefs.shape !== undefined) prefs.shape = model.library.prefs.shape;
    if (model.library.prefs.widthMm !== undefined) prefs.widthMm = model.library.prefs.widthMm;
    if (model.library.prefs.dropFullMm !== undefined) prefs.dropFullMm = model.library.prefs.dropFullMm;
    // A luminaire carries its own params — the variant, the accent tone and the
    // per-variant size overrides (the schema's `prefs`, nothing invented).
    if (model.library.prefs.variant !== undefined) prefs.variant = model.library.prefs.variant;
    if (model.library.prefs.mount !== undefined) prefs.mount = model.library.prefs.mount;
    if (model.library.prefs.accent !== undefined) prefs.accent = model.library.prefs.accent;
    if (model.library.prefs.sizeMm !== undefined) prefs.sizeMm = { ...model.library.prefs.sizeMm };
    // The light's own design pair travels with the slot: a param the sanitiser
    // reads but the writer drops is silently lost on the next edit.
    if (model.library.prefs.cct !== undefined) prefs.cct = model.library.prefs.cct;
    if (model.library.prefs.beamDeg !== undefined) prefs.beamDeg = model.library.prefs.beamDeg;
    // The per-fixture shadow choice (owner, 2026-10-21). Written here because this
    // WRITER is a whitelist as well: the reader was fixed first and the control still
    // snapped back, because the value was being dropped on the way OUT and the next parse
    // had nothing to read.
    if (model.library.prefs.shadow !== undefined) prefs.shadow = model.library.prefs.shadow;
    if (model.library.prefs.lux !== undefined) prefs.lux = model.library.prefs.lux;
    if (model.library.prefs.clipToRoom !== undefined) prefs.clipToRoom = model.library.prefs.clipToRoom;
    if (model.library.prefs.aim !== undefined) prefs.aim = model.library.prefs.aim;
    out.library = { type: model.library.type, model: model.library.model, prefs };
  }
  if (model.ifc) {
    out.ifc = {
      url: model.ifc.url,
      name: model.ifc.name,
      bytes: model.ifc.bytes,
      uploadedAt: model.ifc.uploadedAt,
      scale: model.ifc.scale,
    };
  }
  return out;
}

/**
 * A widget config with this model slot written (or REMOVED with `null`).
 *
 * Pure: the caller hands the result to `PUT /dashboards/widgets/:id`. Removal
 * writes the key as `undefined` rather than deleting it, because every config
 * dialog's answer is MERGED forward by `WidgetEditor.vue`
 * (`{...widget.widgetConfig, ...localConfig}`): a deleted key would let the
 * stored one win, so the removed model would come back on the next save. An
 * `undefined` overwrites it in that merge, and `JSON.stringify` leaves it out of
 * the wire payload — the same rule `DeviceControlConfig`/`SceneConfig` use for
 * their own retired keys.
 */
export function withDeviceModel<T extends Record<string, JsonValue | undefined>>(
  config: T,
  model: DeviceModel3d | null,
): T {
  const next: Record<string, JsonValue | undefined> = { ...config };
  next[DEVICE_MODEL_KEY] = model ? deviceModelJson(model) : undefined;
  return next as T;
}

/**
 * A DEVICE's settings bag with its own slot written — or with the key dropped
 * when the model is removed (card t_6b2c5f01).
 *
 * The hub REPLACES a device's settings bag on a PATCH (`{...settings, ...its own
 * protected keys}`), so the caller sends the whole bag back: this spreads the
 * settings it read and overlays the slot. Removal is a real removal — the key is
 * not in the payload at all — which an `undefined` could not do through the
 * widget-side merge above.
 */
export function withDeviceModelSettings(
  settings: unknown,
  model: DeviceModel3d | null,
): Record<string, unknown> {
  const bag: Record<string, unknown> = isRecord(settings) ? { ...settings } : {};
  if (model) bag[DEVICE_MODEL_KEY] = deviceModelJson(model);
  else delete bag[DEVICE_MODEL_KEY];
  return bag;
}

/**
 * The slot that renders for one widget (card t_6b2c5f01): the DEVICE's own model
 * first — a device carries it in its `settings` bag, exactly like its icon —
 * with the widget's stored slot as the back-compat fallback for the attaches
 * made before device-level storage existed.
 *
 * The widget's own pin is where a DEVICE-level model renders. The geometry is a
 * fact about the device, the placement is a fact about the element that carries
 * it, so a device pinned in two places draws in both, and a widget with no pin
 * draws nothing (there is nowhere to put it — the same rule a widget-level slot
 * has always had). A widget-level slot keeps its own stored placement untouched.
 */
export function resolveDeviceModel(widgetConfig: unknown, deviceSettings: unknown): DeviceModel3d | null {
  const fromDevice = parseDeviceModel(deviceSettings);
  if (fromDevice) {
    const pin = widgetAnchor(widgetConfig);
    if (!pin) return null;
    return { ...fromDevice, placement: { ...fromDevice.placement, origin: [pin[0], pin[1], pin[2]] } };
  }
  return parseDeviceModel(widgetConfig);
}

/** Two slots are the same attach (change detection for the editor's dirty state). */
export function sameDeviceModel(a: DeviceModel3d | null, b: DeviceModel3d | null): boolean {
  if (a === null || b === null) return a === b;
  return JSON.stringify(deviceModelJson(a)) === JSON.stringify(deviceModelJson(b));
}

/** A stable key for a slot: what the renderer rebuilds on. */
export function deviceModelKey(widgetId: string, model: DeviceModel3d, plane: MountingPlane | null = null): string {
  const kind = model.source === 'library' ? `${model.library?.type}|${model.library?.model}` : `ifc|${model.ifc?.url}`;
  const params = model.source === 'library' ? JSON.stringify(model.library?.prefs ?? {}) : String(model.ifc?.scale ?? 1);
  const frame = `${model.placement.origin.join(',')}|${model.placement.yawDeg}|${model.placement.pitchDeg}|${model.placement.widthMm}|${model.placement.dropFullMm}`;
  // The ELEMENT's own plane is part of the frame the meshes are built in, so a
  // re-seated anchor (a re-served revision, a moved window) rebuilds the group
  // (card t_9a056ea5) — without it the layer would keep the basis it was born
  // with, which is the very staleness this card is about.
  const planeKey = plane ? `|plane:${plane.yawDeg.toFixed(4)},${plane.leanDeg.toFixed(4)}` : '';
  return `${widgetId}::${kind}::${params}::${frame}${planeKey}`;
}

// ─── Geometry: the library families, with the operator's params applied ──────

/**
 * The parts of one SHADE family, built from the SAME constants the house's
 * Python modules use (`bimShadeParts.ts`, which pins them against the modules'
 * own output) with the operator's params folded in:
 *
 *   · colour       — a material choice, applied by the renderer (never geometry)
 *   · cassetteHidden — passed straight through (the head box tucks back)
 *   · slatPitchMm  — the jalousie's slat stack, re-spaced (the only param the
 *                    shared builder's constant covers, so it is re-stacked here)
 *   · width/drop   — the frame's own size, metres
 *
 * The LUMINAIRE family is not a shade: it has no frame size and no position to
 * follow, so its parts come from its own builder
 * (`bimLuminaireParts.luminaireParts`, keyed by variant) — hence the shade-only
 * parameter type.
 */
export function libraryParts(
  type: ShadeLibraryType,
  params: { widthM: number; dropM: number; tiltDeg?: number; prefs: LibraryPrefs },
): ShadePartBox[] {
  const styled: StyledShadeParams = {
    widthM: params.widthM,
    dropM: params.dropM,
    tiltDeg: params.tiltDeg,
    cassetteHidden: params.prefs.cassetteHidden,
  };
  const pitch = params.prefs.slatPitchMm;
  const defaultPitch = VENETIAN.slatPitch / 0.001;
  if (type !== 'venetian-blind' || pitch === undefined || Math.abs(pitch - defaultPitch) < 0.5) {
    return shadeParts(type, styled);
  }
  // The slat stack at the operator's pitch: everything else is the shared build.
  const rest = shadeParts(type, styled).filter((part) => !part.key.startsWith('slat-'));
  const usable =
    params.dropM - VENETIAN.headBoxH - VENETIAN.topGap - VENETIAN.bottomRailH - VENETIAN.bottomGap;
  const count = Math.max(1, Math.floor(usable / (pitch / 1000)) + 1);
  const slatLen = params.widthM - 2 * VENETIAN.slatEndGap;
  const tilt = params.tiltDeg ?? -20;
  const firstOffset =
    VENETIAN.headBoxH + VENETIAN.topGap + (VENETIAN.slatDepth / 2) * Math.abs(Math.sin((tilt * Math.PI) / 180));
  const slats: ShadePartBox[] = [];
  for (let i = 0; i < count; i += 1) {
    slats.push({
      key: `slat-${i}`,
      material: 'slat',
      center: [0, VENETIAN.slatY, -(firstOffset + i * (pitch / 1000))],
      size: [slatLen, VENETIAN.slatDepth, VENETIAN.slatThick],
      tiltXDeg: tilt,
    });
  }
  return [...rest, ...slats];
}

// ─── The shade's own frame on the device model (card t_ad566d07) ─────────────

/**
 * The DEVICE-MODEL frame a shade is built in, and why the numbers move.
 *
 * The owner's report (2026-10-12): the shade models sat at the window's CENTRE
 * and read as a blob at mid-window — *"the center of the icon widget being at
 * the center of collapes shuttuter. but it should be at the centter of expaned
 * shutter.."*. The slot's anchor IS the element's pin, and for a cover the
 * pipeline's own pin is the WINDOW's centre (the pipeline's `widgets.py`
 * anchors a cover on its opening) — so the geometry has to be built around
 * that point, not hung off it:
 *
 *   · `+X` — along the window's width;
 *   · `+Y` — UP the wall (the window's height direction; up-slope on a roof);
 *   · `+Z` — the outward window normal;
 *   · origin — the CENTRE OF THE FULLY-EXPANDED shade, so the head box /
 *     cassette stands `+dropFull/2` above it and a full drop reaches
 *     `−dropFull/2` below. The icon button the operator placed lands on the
 *     middle of the extended shade, which is what the owner asked for.
 *
 * The part boxes come from the shared Python port (`bimShadeParts.ts`), whose
 * own contract is `+X` width, `+Y` OUT, `+Z` up with the head at `z = 0` — the
 * frame the hub's authored twins are built in. `shadePartsInDeviceFrame` is
 * that frame re-expressed here, one axis swap and one shift, and the swap is
 * exact: `(x, y, z) → (x, z + dropFull/2, y)` sends a box to a box (centre and
 * size swap their last two numbers), and a rotation about `X` by θ becomes one
 * by −θ (a swap mirrors the pair the rotation acts in), so the meshes land
 * where the shared build put them plus the shift — nothing else moves.
 */
export function shadePartsInDeviceFrame(parts: readonly ShadePartBox[], fullDropM: number): ShadePartBox[] {
  const half = (Number.isFinite(fullDropM) ? fullDropM : 0) / 2;
  return parts.map((part) => ({
    key: part.key,
    material: part.material,
    center: [part.center[0], part.center[2] + half, part.center[1]],
    size: [part.size[0], part.size[2], part.size[1]],
    ...(part.tiltXDeg === undefined ? {} : { tiltXDeg: -part.tiltXDeg }),
  }));
}

/**
 * A shade family's parts, in the device-model frame above, at one size/state.
 * `dropFullM` is the frame's own full drop (the placement's `dropFullMm`), NOT
 * the state's drop: the anchor is the EXPANDED shade's centre, so it must not
 * move as the shade rolls up — `dropM` is only how far the fabric/slats hang.
 */
/**
 * One family's part boxes in the device-model frame — the SHADE families from
 * `bimShadeParts` (shifted into that frame by `shadePartsInDeviceFrame`) and the
 * GARAGE DOOR from `bimGarageDoorParts`, which authors its boxes in the frame
 * directly (a door's pin IS the middle of the closed door, so the shift the
 * shades need — theirs anchors on the expanded shade's centre from a product
 * frame whose head is at 0 — has nothing to do for a door and is not applied).
 */
export function deviceModelParts(
  type: DeviceModelPartsType,
  params: { widthM: number; dropM: number; dropFullM: number; tiltDeg?: number; prefs: LibraryPrefs },
): FamilyPartBox[] {
  if (type === 'garage-door') {
    return garageDoorParts({
      widthM: params.widthM,
      dropM: params.dropM,
      dropFullM: params.dropFullM,
      prefs: params.prefs,
    });
  }
  if (type === 'sliding-gate') {
    return slidingGateParts({
      widthM: params.widthM,
      dropM: params.dropM,
      dropFullM: params.dropFullM,
      prefs: params.prefs,
    });
  }
  if (type === 'swing-gate') {
    return swingGateParts({
      widthM: params.widthM,
      dropM: params.dropM,
      dropFullM: params.dropFullM,
      prefs: params.prefs,
    });
  }
  return shadePartsInDeviceFrame(libraryParts(type, params), params.dropFullM);
}

/** Every material key a family's parts can name (the shades', the door's, the gates'). */
export type FamilyPartMaterial = ShadePartMaterial | GarageDoorPartMaterial | GatePartMaterial;

/** A part box in the device-model frame (X width, Y up, Z out), metres. */
export interface FamilyPartBox {
  key: string;
  material: FamilyPartMaterial;
  center: Vec3;
  size: Vec3;
  /** Slats: the tilt about the local X (the shade's own axis). */
  tiltXDeg?: number;
  /** A turning part (a swing gate's leaf): the yaw about the vertical axis. */
  yawDeg?: number;
}

// ─── State: what the device's live values do to it ──────────────────────────

/** A rolled-up cover keeps a short stack — the house pipeline's own floor. */
export const MIN_DEVICE_MODEL_DROP_MM = 150;
const DEFAULT_TILT_OPEN_DEG = -20;
const DEFAULT_TILT_CLOSED_DEG = -75;

export interface DeviceModelState {
  /** position % as the hub reports it (0 = up, 100 = fully down). */
  position: number;
  tilt: number | null;
  /** The drop the model is built at right now, mm (clamped like the hub's own). */
  dropMm: number;
  /** Jalousie only: the slat angle now, degrees. */
  tiltDeg: number | null;
}

/** A cover's live position (%) -> the drop, clamped to the frame's own size. */
export function deviceModelDropMm(dropFullMm: number, position: number): number {
  const fraction = Math.min(1, Math.max(0, position / 100));
  return Math.min(dropFullMm, Math.max(MIN_DEVICE_MODEL_DROP_MM, dropFullMm * fraction));
}

/** A jalousie's tilt (%) -> the slat angle, open … closed. */
export function deviceModelTiltDeg(tiltPct: number): number {
  const fraction = Math.min(1, Math.max(0, tiltPct / 100));
  return DEFAULT_TILT_OPEN_DEG + fraction * (DEFAULT_TILT_CLOSED_DEG - DEFAULT_TILT_OPEN_DEG);
}

/**
 * Resolve the live values into geometry numbers. A family that has no tilt (zip
 * screens, roof shutters) answers a null angle: their geometry has no slats.
 * Shades only — a luminaire follows the light's POWER (it glows), which the
 * renderer reads from `bimLuminaireSpec.lightGlowState`.
 */
export function deviceModelState(
  type: DeviceModelPartsType,
  dropFullMm: number,
  position: number,
  tilt: number | null,
): DeviceModelState {
  const jalousie = type === 'venetian-blind';
  // A GARAGE DOOR reads the other way round: its `position` is how far it is
  // OPEN (0 = closed, 100 = fully open — `utils/garageDoorState.ts`), where a
  // cover's is how far it is DOWN. The drop is the same idea in both frames:
  // how much product hangs from the top, which is why the door inverts here
  // rather than in a second state fold.
  const hangingPct = familyKind(type) === 'access' ? 100 - position : position;
  return {
    position,
    tilt: jalousie ? tilt : null,
    dropMm: deviceModelDropMm(dropFullMm, hangingPct),
    tiltDeg: jalousie && tilt !== null ? deviceModelTiltDeg(tilt) : null,
  };
}

// ─── Imported files ─────────────────────────────────────────────────────────

/**
 * The scale an imported model is rendered at, from its own bounding box.
 *
 * A file authored in millimetres (the common case for a downloaded device model:
 * a 1.2 m lamp arrives as 1200 units) is 1000× the scene's metres, so a model
 * whose longest side is more than 1000× a house — or simply more than 50 units —
 * is read as millimetres. Deliberately coarse: the operator has a scale field
 * for the case the guess gets wrong, and a wrong guess stays visible (a 1000×
 * lamp is unmistakable) rather than subtle.
 */
export function unitScaleForBounds(size: Vec3): number {
  const longest = Math.max(Math.abs(size[0]), Math.abs(size[1]), Math.abs(size[2]));
  if (!Number.isFinite(longest) || longest <= 0) return 1;
  return longest > 50 ? 0.001 : 1;
}

/** Human text for a byte count (the chooser's own line). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}
